import React from 'react';

import { useSiteMetadata, withImage } from '@lucid/hooks';
import { withMetatags, getLanguage } from '@lucid/utils';

import SEO from '../seo/seo';
import type { Lucid } from '../../types/types';

type LucidHeadProps<T = Lucid.Directus.Custom.DirectusData> = {
  data?: T;
  pageContext: Lucid.Context.Keys & Partial<Lucid.Context.Pagination>;
  location?: Partial<Location>;
  templateMetatags?: Lucid.Context.Metatag.Fields[];
};

const imageMetatags = ['image_src', 'og:image', 'twitter:image'];

const metatagsToObject = (metatags: Lucid.Context.Metatag.Fields[]): Record<string, string> =>
  metatags.reduce(
    (acc, mt) => (typeof mt.key === 'string' && !!mt.value ? { ...acc, [mt.key]: mt.value } : acc),
    {} as Record<string, string>,
  );

const objectToMetatags = (metatags: Record<string, string>): Lucid.Context.Metatag.Fields[] =>
  Object.entries(metatags).map(([key, value]) => ({ key, value }));

// These custom OpenGraph and Twitter meta values will be created based on the default values provided
// example: "description" will be duplicated into "og:description" and "twitter:description"
const populateAliasMetatags = (metatags: Record<string, string>, shouldUseTitleAsAlias: boolean = false) => {
  const metatagAliasGroups = [
    ['title', 'og:title', 'twitter:title'],
    ['description', 'og:description', 'twitter:description'],
    imageMetatags,
    ['og:image:alt', 'twitter:image:alt'],
  ];

  const missingMetatags = metatagAliasGroups.reduce(
    (acc, aliasGroup) => {
      const aliases = aliasGroup.filter(alias => alias !== 'title' || shouldUseTitleAsAlias);
      const foundValues = Object.keys(metatags).filter(key => aliases.includes(key));

      if (foundValues.length > 0 && foundValues.length < aliases.length) {
        const missingKeys = aliases.filter(mirror => !foundValues.some(key => key === mirror));

        const missingMetatags = missingKeys.reduce(
          (previous, key) => {
            if (foundValues[0] === 'title') {
              return { ...previous, [key]: metatags[foundValues[0]].replace(/ \| Lucid.*/, '') };
            }

            return { ...previous, [key]: metatags[foundValues[0]] };
          },
          {} as Record<string, string>,
        );

        return { ...acc, ...missingMetatags };
      }

      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    ...missingMetatags,
    ...metatags,
  };
};

const processImages = (metatags: Record<string, string>) => ({
  ...metatags,
  ...Object.keys(metatags)
    .filter(key => imageMetatags.includes(key))
    .map(key => ({
      key,
      value: withImage({ filename_disk: metatags[key] } as Lucid.Directus.Generated.Directus_Files),
    }))
    .reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}),
  schema_markup: metatags.schema_markup?.replaceAll(
    /"(url|image)":\s*"([^"\s]*?\.(?:png|jpe?g|gif|webp|svg)[^"]*)"/gi,
    (_, label, url) => `"${label}": "${withImage({ filename_disk: url } as Lucid.Directus.Generated.Directus_Files)}"`,
  ),
});

const LucidHead = <T,>({ pageContext, location, data }: LucidHeadProps<T>) => {
  const { page, metatags: contextMetatags, pageType, nid, shortCode, product, dateUpdated } = pageContext;
  const { product: defaultProduct } = useSiteMetadata();

  const extendMetatags = (metatags: Record<string, string>) => {
    const sitenameMap = {
      co: 'Lucid Software',
      edu: 'Lucid for Education',
      chart: 'Lucidchart',
      spark: 'Lucidspark',
    };

    // these values will be added to all pages by default, but can be overwritten in 'createMetatags.ts' on a per-template basis
    const manualMetatags = {
      'og:locale': getLanguage(shortCode)['code'].replace('-', '_'),
      'twitter:site': '@LucidSoftware',
      'twitter:site:id': '66383448',
      'og:site_name': sitenameMap[product] || 'Lucid Software',
      'og:type': 'website',
    };

    const conditionalMetatags = {};

    // set article:modified_time and article schema markup if the og:type is an article
    if (metatags['og:type'] === 'article' && !metatags['schema_markup']) {
      if (dateUpdated) conditionalMetatags['article:modified_time'] = new Date(dateUpdated).toISOString();

      conditionalMetatags['schema_markup'] = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        dateModified: dateUpdated ? new Date(dateUpdated).toISOString() : '',
        headline: metatags['og:title'],
        description: metatags['description'],
        image: metatags['og:image'],
      });
    }

    // set twitter:card if twitter:image is set
    if (metatags['twitter:image']) {
      conditionalMetatags['twitter:card'] = 'summary_large_image';
    }

    // set noindex on any spark, chart, or edu pages that have been migrated into co
    if (defaultProduct === 'co' && !['scale', 'co', 'spark'].includes(product)) {
      conditionalMetatags['robots'] = 'noindex, nofollow';
    }

    // set noindex on any pages that are scheduled to be released in the future
    if (pageContext.isScheduledRelease) {
      conditionalMetatags['robots'] = 'noindex, nofollow';
    }

    // set title and robots on any pages that are paginated
    if (page && page > 1) {
      conditionalMetatags['title'] = `Page ${page} for ${metatags.title}`;
      conditionalMetatags['robots'] = 'follow, noindex';
    }

    return populateAliasMetatags({ ...manualMetatags, ...metatags, ...conditionalMetatags }, true);
  };

  const hasDirectusQueryData = (data: unknown): data is { directus: { node: Lucid.Directus.Custom.Node } } =>
    typeof data === 'object' &&
    data !== null &&
    'directus' in data &&
    typeof (data as { directus?: unknown }).directus === 'object' &&
    (data as { directus: { node?: unknown } }).directus?.node !== undefined;

  const templateMetatags = withMetatags.create({
    template: pageType,
    data: hasDirectusQueryData(data) ? data.directus.node : data,
  });

  // metatags from the context (Directus) should overwrite the template default metatags
  const nodeMetatags: Record<string, string> = {
    ...populateAliasMetatags(metatagsToObject(templateMetatags || [])),
    ...populateAliasMetatags(metatagsToObject(contextMetatags || [])),
  };

  const { schema_markup: schemaMarkup, ...allMetatags }: Record<string, string> = processImages(
    extendMetatags(nodeMetatags),
  );

  return (
    <SEO
      key={`seo-${pageType}-${nid ?? '1'}`}
      location={location}
      metatags={objectToMetatags(allMetatags)}
      product={product}
      schemaMarkup={schemaMarkup}
      shortCode={shortCode}
    />
  );
};

export default LucidHead;
